<script>
	import { createEventDispatcher, onMount, onDestroy } from "svelte";
	import Icon from "./icons/Icon.svelte";
	import {
		fetchMasterCatalogNichos,
		fetchMasterCatalogProducts,
		resolveCatalogImageUrl
	} from "../services/api";

	export let open = false;
	export let currentForm = {};
	export let storeCategories = [];

	const dispatch = createEventDispatcher();

	// Estados da consulta e listagem
	let nichos = [];
	let selectedNicho = "all";
	let searchQuery = "";
	let searchTimeout = null;
	let currentPage = 1;
	const pageSize = 24;
	let totalItems = 0;
	let totalPages = 1;
	let items = [];
	let loading = false;
	let error = null;

	// Estado do fluxo de seleção / preview
	let selectedProduct = null;
	let prefillOptions = {
		name: true,
		image: true,
		category: true,
		suggestedPrice: false
	};

	// Imagens com erro de carregamento (fallback limpo)
	let brokenImages = new Set();

	// Monitora abertura do modal
	$: if (open) {
		selectedProduct = null;
		if (nichos.length === 0) {
			loadNichos();
		}
		loadProducts();
	}

	onMount(() => {
		if (typeof window !== "undefined") {
			window.addEventListener("keydown", handleGlobalKeydown);
		}
	});

	onDestroy(() => {
		if (typeof window !== "undefined") {
			window.removeEventListener("keydown", handleGlobalKeydown);
		}
		if (searchTimeout) clearTimeout(searchTimeout);
	});

	function handleGlobalKeydown(e) {
		if (open && e.key === "Escape") {
			handleClose();
		}
	}

	async function loadNichos() {
		try {
			const res = await fetchMasterCatalogNichos();
			if (res && Array.isArray(res.nichos)) {
				nichos = res.nichos;
			}
		} catch (err) {
			console.warn("[Catálogo Mestre] Erro ao obter nichos:", err.message);
		}
	}

	async function loadProducts() {
		loading = true;
		error = null;
		try {
			const data = await fetchMasterCatalogProducts({
				q: searchQuery,
				nicho: selectedNicho,
				page: currentPage,
				limit: pageSize
			});
			items = data.items || [];
			totalItems = (data.pagination && data.pagination.total !== undefined) ? data.pagination.total : (data.total || 0);
			totalPages = (data.pagination && data.pagination.total_pages !== undefined) ? data.pagination.total_pages : (data.totalPages || 1);
		} catch (err) {
			console.error("[Catálogo Mestre] Erro na busca:", err);
			error = "Não foi possível carregar os produtos do catálogo. O cadastro manual continua funcionando normalmente.";
			items = [];
		} finally {
			loading = false;
		}
	}

	function handleSearchInput(e) {
		searchQuery = e.target.value;
		if (searchTimeout) clearTimeout(searchTimeout);
		searchTimeout = setTimeout(() => {
			currentPage = 1;
			loadProducts();
		}, 300);
	}

	function clearSearch() {
		searchQuery = "";
		currentPage = 1;
		loadProducts();
	}

	function handleSelectNicho(nichoId) {
		if (selectedNicho === nichoId) return;
		selectedNicho = nichoId;
		currentPage = 1;
		loadProducts();
	}

	function handlePrevPage() {
		if (currentPage > 1) {
			currentPage--;
			loadProducts();
		}
	}

	function handleNextPage() {
		if (currentPage < totalPages) {
			currentPage++;
			loadProducts();
		}
	}

	// Inicia preview do produto selecionado
	function openProductPreview(product) {
		selectedProduct = product;
		const hasExistingPrice = currentForm && currentForm.price && Number(currentForm.price) > 0;
		// Se a loja já tiver preço preenchido, mantém desmarcado para proteger o preço da loja
		// Se o campo estiver vazio e houver preço sugerido, sugere ativá-lo
		prefillOptions = {
			name: true,
			image: true,
			category: true,
			suggestedPrice: !hasExistingPrice && Boolean(product.preco_sugerido)
		};
	}

	function backToSearch() {
		selectedProduct = null;
	}

	// Busca se a loja já tem categoria equivalente
	function findMatchingStoreCategory(catalogCategory) {
		if (!catalogCategory || !Array.isArray(storeCategories) || storeCategories.length === 0) {
			return null;
		}
		const cleanCat = catalogCategory.toLowerCase().trim();
		return storeCategories.find(c => {
			const cName = (c.name || "").toLowerCase().trim();
			return cName === cleanCat || cName.includes(cleanCat) || cleanCat.includes(cName);
		}) || null;
	}

	function handleConfirmSelection() {
		if (!selectedProduct) return;

		const matchedCat = findMatchingStoreCategory(selectedProduct.categoria);

		dispatch("select", {
			product: selectedProduct,
			options: { ...prefillOptions },
			matchedCategoryId: matchedCat ? matchedCat.id : null,
			matchedCategoryName: matchedCat ? matchedCat.name : null
		});

		handleClose();
	}

	function handleClose() {
		selectedProduct = null;
		dispatch("close");
	}

	function handleImageError(id) {
		brokenImages.add(id);
		brokenImages = new Set(brokenImages);
	}

	function formatCurrency(val) {
		if (val === null || val === undefined || isNaN(val)) return "Sob consulta";
		return Number(val).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
	}
</script>

{#if open}
	<div class="modal-overlay-catalog" on:click={handleClose}>
		<div class="modal-box-catalog" on:click|stopPropagation role="dialog" aria-modal="true" aria-labelledby="catalog-modal-title">
			
			<!-- HEADER DO MODAL -->
			<header class="catalog-header">
				<div class="header-title-wrap">
					<span class="catalog-badge-icon" aria-hidden="true">
						<Icon name="book-open" size={18} color="#FF6A00" />
					</span>
					<div>
						<h2 id="catalog-modal-title" class="catalog-title">Catálogo Mestre de Produtos</h2>
						<p class="catalog-subtitle">536 produtos profissionais com fotos em alta resolução prontas para o cardápio</p>
					</div>
				</div>
				<button type="button" class="btn-close-catalog" on:click={handleClose} aria-label="Fechar Catálogo Mestre">
					<Icon name="x" size={18} />
				</button>
			</header>

			<!-- CORPO: LISTAGEM OU PREVIEW -->
			<div class="catalog-content">
				{#if !selectedProduct}
					<!-- BARRA DE PESQUISA & FILTROS -->
					<div class="catalog-toolbar">
						<div class="search-input-wrapper">
							<span class="search-icon-slot" aria-hidden="true">
								<Icon name="search" size={16} color="#94A3B8" />
							</span>
							<input
								type="text"
								class="catalog-search-input"
								placeholder="Buscar por nome, marca ou nicho (ex: Coca-Cola, Heineken, Skol...)"
								value={searchQuery}
								on:input={handleSearchInput}
								aria-label="Pesquisar no catálogo mestre"
							/>
							{#if searchQuery}
								<button type="button" class="btn-clear-search" on:click={clearSearch} aria-label="Limpar pesquisa">
									<Icon name="x" size={14} />
								</button>
							{/if}
						</div>

						<!-- FILTRO HORIZONTAL DE NICHOS -->
						<div class="nichos-pill-bar" role="tablist" aria-label="Filtrar por nicho">
							<button
								type="button"
								class="nicho-pill {selectedNicho === 'all' ? 'active' : ''}"
								on:click={() => handleSelectNicho('all')}
								role="tab"
								aria-selected={selectedNicho === 'all'}
							>
								<span>Todos os Nichos</span>
								<span class="nicho-count">{totalItems > 0 && selectedNicho === 'all' ? totalItems : '536'}</span>
							</button>

							{#each nichos as n}
								<button
									type="button"
									class="nicho-pill {selectedNicho === n.id ? 'active' : ''}"
									on:click={() => handleSelectNicho(n.id)}
									role="tab"
									aria-selected={selectedNicho === n.id}
								>
									<span>{n.nome}</span>
									<span class="nicho-count">{n.quantidade !== undefined ? n.quantidade : n.total}</span>
								</button>
							{/each}
						</div>
					</div>

					<!-- ÁREA DE RESULTADOS -->
					<div class="catalog-results-area">
						{#if loading}
							<!-- SKELETON LOADING -->
							<div class="catalog-grid" aria-busy="true">
								{#each Array(8) as _}
									<div class="catalog-card skeleton-card">
										<div class="skeleton-img shimmer"></div>
										<div class="skeleton-meta">
											<div class="skeleton-line shimmer w-60"></div>
											<div class="skeleton-line shimmer w-90"></div>
											<div class="skeleton-line shimmer w-40"></div>
										</div>
									</div>
								{/each}
							</div>
						{:else if error}
							<!-- ESTADO DE ERRO -->
							<div class="catalog-empty-state error-state">
								<div class="empty-icon-wrap" aria-hidden="true">
									<Icon name="alert-triangle" size={32} color="#EF4444" />
								</div>
								<h3>Ops! Ocorreu um problema</h3>
								<p>{error}</p>
								<div class="empty-actions">
									<button type="button" class="btn-action-retry" on:click={loadProducts}>
										Tentar novamente
									</button>
									<button type="button" class="btn-action-close" on:click={handleClose}>
										Continuar cadastro manual
									</button>
								</div>
							</div>
						{:else if items.length === 0}
							<!-- ESTADO VAZIO -->
							<div class="catalog-empty-state">
								<div class="empty-icon-wrap" aria-hidden="true">
									<Icon name="search" size={32} color="#94A3B8" />
								</div>
								<h3>Nenhum produto encontrado</h3>
								<p>
									{#if searchQuery}
										Não encontramos itens para "<strong>{searchQuery}</strong>".
									{:else}
										Nenhum produto cadastrado neste nicho.
									{/if}
									Tente buscar por outro nome ou selecione outro nicho acima.
								</p>
								{#if searchQuery || selectedNicho !== 'all'}
									<button type="button" class="btn-action-clear-filter" on:click={() => { searchQuery = ''; selectedNicho = 'all'; currentPage = 1; loadProducts(); }}>
										Limpar filtros e ver todos
									</button>
								{/if}
							</div>
						{:else}
							<!-- GRID DE CARDS DE PRODUTOS -->
							<div class="catalog-grid">
								{#each items as item (item.id)}
									<div class="catalog-card" on:click={() => openProductPreview(item)}>
										<div class="card-thumb-container">
											{#if brokenImages.has(item.id)}
												<div class="card-thumb-fallback">
													<Icon name="package" size={32} color="#CBD5E1" />
													<span>Foto indisponível</span>
												</div>
											{:else}
												<img
													src={resolveCatalogImageUrl(item.foto)}
													alt={item.nome}
													class="card-thumb-img"
													loading="lazy"
													on:error={() => handleImageError(item.id)}
												/>
											{/if}
											{#if item.marca}
												<span class="card-brand-badge">{item.marca}</span>
											{/if}
										</div>

										<div class="card-body">
											<div class="card-cat-tag">{item.categoria}</div>
											<h4 class="card-title" title={item.nome}>{item.nome}</h4>
											
											<div class="card-footer-info">
												{#if item.preco_sugerido}
													<div class="card-price-ref">
														<span class="price-label">Ref. atacado:</span>
														<span class="price-val">{formatCurrency(item.preco_sugerido)}</span>
													</div>
												{:else}
													<div class="card-clean-tag">
														<span>Foto em HD</span>
													</div>
												{/if}
												<button
													type="button"
													class="btn-card-select"
													on:click|stopPropagation={() => openProductPreview(item)}
													aria-label="Selecionar {item.nome}"
												>
													<span>Usar</span>
													<Icon name="chevron-right" size={14} />
												</button>
											</div>
										</div>
									</div>
								{/each}
							</div>
						{/if}
					</div>

					<!-- PAGINAÇÃO -->
					{#if !loading && totalPages > 1}
						<footer class="catalog-pagination-bar">
							<span class="pagination-info">
								Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong> ({totalItems} produtos)
							</span>
							<div class="pagination-controls">
								<button
									type="button"
									class="btn-page-step"
									disabled={currentPage <= 1}
									on:click={handlePrevPage}
									aria-label="Página anterior"
								>
									<Icon name="chevron-up" size={14} />
									<span>Anterior</span>
								</button>
								<button
									type="button"
									class="btn-page-step"
									disabled={currentPage >= totalPages}
									on:click={handleNextPage}
									aria-label="Próxima página"
								>
									<span>Próxima</span>
									<Icon name="chevron-down" size={14} />
								</button>
							</div>
						</footer>
					{/if}

				{:else}
					<!-- ETAPA DE PREVIEW & CONFIRMAÇÃO DO PRODUTO SELECIONADO -->
					<div class="catalog-preview-container">
						<div class="preview-header-nav">
							<button type="button" class="btn-back-to-list" on:click={backToSearch}>
								<Icon name="chevron-right" size={14} />
								<span>Voltar para a lista de produtos</span>
							</button>
						</div>

						<div class="preview-main-layout">
							<!-- COLUNA DA ESQUERDA: FOTO DO PRODUTO -->
							<div class="preview-photo-col">
								<div class="preview-large-photo-box">
									{#if brokenImages.has(selectedProduct.id)}
										<div class="preview-photo-fallback">
											<Icon name="package" size={48} color="#CBD5E1" />
											<span>Foto indisponível</span>
										</div>
									{:else}
										<img
											src={resolveCatalogImageUrl(selectedProduct.foto)}
											alt={selectedProduct.nome}
											class="preview-large-img"
										/>
									{/if}
								</div>
								<div class="preview-photo-caption">
									<Icon name="check" size={14} color="#059669" />
									<span>Foto oficial com fundo tratado e alta resolução</span>
								</div>
							</div>

							<!-- COLUNA DA DIREITA: DADOS E CONFIGURAÇÃO DE IMPORTAÇÃO -->
							<div class="preview-details-col">
								<div class="preview-meta-header">
									<div class="meta-tags-row">
										<span class="preview-tag-cat">{selectedProduct.categoria}</span>
										{#if selectedProduct.marca}
											<span class="preview-tag-brand">Marca: {selectedProduct.marca}</span>
										{/if}
										{#if selectedProduct.sku}
											<span class="preview-tag-sku">SKU/EAN: {selectedProduct.sku}</span>
										{/if}
									</div>
									<h3 class="preview-product-name">{selectedProduct.nome}</h3>
								</div>

								{#if selectedProduct.preco_sugerido}
									<!-- ALERTA EDUCATIVO SOBRE PREÇO DE REFERÊNCIA -->
									<div class="preview-price-notice-card">
										<div class="notice-icon-col">
											<Icon name="wallet" size={18} color="#FF6A00" />
										</div>
										<div class="notice-text-col">
											<div class="notice-title-row">
												<strong>Preço de Referência do Catálogo:</strong>
												<span class="notice-price-highlight">{formatCurrency(selectedProduct.preco_sugerido)}</span>
											</div>
											<p>
												Este valor é uma referência de atacado/mercado. O preço final de venda para o seu cliente é 100% definido por você e poderá ser ajustado livremente no formulário.
											</p>
										</div>
									</div>
								{/if}

								<!-- OPÇÕES DE PRÉ-PREENCHIMENTO -->
								<div class="preview-checklist-box">
									<h4 class="checklist-title">Selecione o que deseja preencher automaticamente:</h4>

									<label class="check-option-row">
										<input type="checkbox" bind:checked={prefillOptions.name} />
										<div class="check-option-info">
											<strong>Nome do Produto</strong>
											<small>{selectedProduct.nome}</small>
										</div>
									</label>

									<label class="check-option-row">
										<input type="checkbox" bind:checked={prefillOptions.image} />
										<div class="check-option-info">
											<strong>Foto em Alta Resolução</strong>
											<small>Pronta para o cardápio digital e catálogo do WhatsApp</small>
										</div>
									</label>

									<label class="check-option-row">
										<input type="checkbox" bind:checked={prefillOptions.category} />
										<div class="check-option-info">
											<strong>Categoria do Estabelecimento</strong>
											{#if findMatchingStoreCategory(selectedProduct.categoria)}
												<small>Sugerido: <em>{findMatchingStoreCategory(selectedProduct.categoria).name}</em></small>
											{:else}
												<small>Manterá a categoria atualmente selecionada na sua loja</small>
											{/if}
										</div>
									</label>

									{#if selectedProduct.preco_sugerido}
										<label class="check-option-row">
											<input
												type="checkbox"
												bind:checked={prefillOptions.suggestedPrice}
											/>
											<div class="check-option-info">
												<strong>Preencher Preço Sugerido ({formatCurrency(selectedProduct.preco_sugerido)})</strong>
												<small>Preenche como ponto de partida para conferência no seu formulário</small>
											</div>
										</label>
									{/if}
								</div>

								<!-- AÇÕES DO PREVIEW -->
								<div class="preview-actions-bar">
									<button type="button" class="btn-cancel-preview" on:click={backToSearch}>
										Voltar
									</button>
									<button type="button" class="btn-confirm-apply" on:click={handleConfirmSelection}>
										<Icon name="check" size={16} color="#FFFFFF" />
										<span>Usar este Produto no Cadastro</span>
									</button>
								</div>
							</div>
						</div>
					</div>
				{/if}
			</div>

		</div>
	</div>
{/if}

<style>
	/* MODAL BACKDROP */
	.modal-overlay-catalog {
		position: fixed;
		inset: 0;
		background: rgba(15, 23, 42, 0.65);
		backdrop-filter: blur(6px);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1200;
		padding: 16px;
	}

	.modal-box-catalog {
		width: 1040px;
		max-width: 100%;
		height: 88vh;
		max-height: 850px;
		background: #FFFFFF;
		border-radius: 16px;
		box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	/* HEADER */
	.catalog-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 18px 24px;
		background: #FFFFFF;
		border-bottom: 1px solid #E2E8F0;
		flex-shrink: 0;
	}

	.header-title-wrap {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.catalog-badge-icon {
		width: 38px;
		height: 38px;
		border-radius: 10px;
		background: #FFF7ED;
		border: 1px solid #FFEDD5;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.catalog-title {
		margin: 0;
		font-size: 17px;
		font-weight: 800;
		color: #0F172A;
		letter-spacing: -0.01em;
	}

	.catalog-subtitle {
		margin: 2px 0 0;
		font-size: 12px;
		color: #64748B;
	}

	.btn-close-catalog {
		background: transparent;
		border: none;
		color: #94A3B8;
		padding: 8px;
		border-radius: 8px;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all 0.15s ease;
	}

	.btn-close-catalog:hover {
		background: #F1F5F9;
		color: #0F172A;
	}

	/* CONTENT WRAPPER */
	.catalog-content {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
		background: #F8FAFC;
	}

	/* TOOLBAR: PESQUISA E NICHOS */
	.catalog-toolbar {
		padding: 16px 24px;
		background: #FFFFFF;
		border-bottom: 1px solid #E2E8F0;
		display: flex;
		flex-direction: column;
		gap: 12px;
		flex-shrink: 0;
	}

	.search-input-wrapper {
		position: relative;
		display: flex;
		align-items: center;
		width: 100%;
	}

	.search-icon-slot {
		position: absolute;
		left: 14px;
		pointer-events: none;
		display: flex;
		align-items: center;
	}

	.catalog-search-input {
		width: 100%;
		height: 42px;
		padding: 0 40px 0 42px;
		background: #F8FAFC;
		border: 1px solid #CBD5E1;
		border-radius: 10px;
		font-size: 14px;
		color: #0F172A;
		transition: all 0.15s ease;
		box-sizing: border-box;
	}

	.catalog-search-input:focus {
		outline: none;
		border-color: #FF6A00;
		background: #FFFFFF;
		box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.12);
	}

	.btn-clear-search {
		position: absolute;
		right: 12px;
		background: transparent;
		border: none;
		color: #94A3B8;
		cursor: pointer;
		padding: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 4px;
	}

	.btn-clear-search:hover {
		color: #0F172A;
		background: #E2E8F0;
	}

	/* NICHOS PILLS */
	.nichos-pill-bar {
		display: flex;
		align-items: center;
		gap: 8px;
		overflow-x: auto;
		padding-bottom: 4px;
		scrollbar-width: thin;
	}

	.nichos-pill-bar::-webkit-scrollbar {
		height: 4px;
	}

	.nichos-pill-bar::-webkit-scrollbar-thumb {
		background: #CBD5E1;
		border-radius: 4px;
	}

	.nicho-pill {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 12px;
		background: #F1F5F9;
		border: 1px solid #E2E8F0;
		border-radius: 20px;
		font-size: 12px;
		font-weight: 600;
		color: #475569;
		cursor: pointer;
		white-space: nowrap;
		transition: all 0.15s ease;
		flex-shrink: 0;
	}

	.nicho-pill:hover {
		background: #E2E8F0;
		color: #0F172A;
	}

	.nicho-pill.active {
		background: #0F172A;
		border-color: #0F172A;
		color: #FFFFFF;
	}

	.nicho-count {
		font-size: 10px;
		font-weight: 700;
		padding: 1px 6px;
		border-radius: 10px;
		background: rgba(0, 0, 0, 0.08);
	}

	.nicho-pill.active .nicho-count {
		background: rgba(255, 255, 255, 0.2);
		color: #FFFFFF;
	}

	/* RESULTS AREA */
	.catalog-results-area {
		flex: 1;
		overflow-y: auto;
		padding: 20px 24px;
	}

	/* GRID DE PRODUTOS */
	.catalog-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 16px;
	}

	.catalog-card {
		background: #FFFFFF;
		border: 1px solid #E2E8F0;
		border-radius: 12px;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		cursor: pointer;
		transition: all 0.2s ease;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
	}

	.catalog-card:hover {
		border-color: #FF6A00;
		box-shadow: 0 8px 16px -4px rgba(255, 106, 0, 0.12);
		transform: translateY(-2px);
	}

	.card-thumb-container {
		position: relative;
		width: 100%;
		padding-top: 80%; /* 5:4 aspect ratio */
		background: #F8FAFC;
		overflow: hidden;
		border-bottom: 1px solid #F1F5F9;
	}

	.card-thumb-img {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: contain;
		padding: 8px;
		transition: transform 0.25s ease;
	}

	.catalog-card:hover .card-thumb-img {
		transform: scale(1.04);
	}

	.card-thumb-fallback {
		position: absolute;
		inset: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 6px;
		color: #94A3B8;
		font-size: 11px;
	}

	.card-brand-badge {
		position: absolute;
		top: 8px;
		left: 8px;
		padding: 2px 8px;
		background: rgba(15, 23, 42, 0.82);
		backdrop-filter: blur(4px);
		color: #FFFFFF;
		font-size: 10px;
		font-weight: 700;
		border-radius: 6px;
		letter-spacing: 0.02em;
	}

	.card-body {
		padding: 12px 14px;
		display: flex;
		flex-direction: column;
		flex: 1;
		gap: 6px;
	}

	.card-cat-tag {
		font-size: 10px;
		font-weight: 700;
		color: #FF6A00;
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	.card-title {
		margin: 0;
		font-size: 13px;
		font-weight: 700;
		color: #0F172A;
		line-height: 1.35;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		min-height: 35px;
	}

	.card-footer-info {
		margin-top: auto;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-top: 8px;
		border-top: 1px solid #F1F5F9;
	}

	.card-price-ref {
		display: flex;
		flex-direction: column;
	}

	.card-clean-tag {
		display: inline-flex;
		align-items: center;
		padding: 2px 7px;
		background: #F1F5F9;
		color: #64748B;
		border-radius: 4px;
		font-size: 10px;
		font-weight: 600;
	}

	.price-label {
		font-size: 9px;
		font-weight: 600;
		color: #94A3B8;
		text-transform: uppercase;
	}

	.price-val {
		font-size: 12px;
		font-weight: 800;
		color: #0F172A;
	}

	.btn-card-select {
		display: inline-flex;
		align-items: center;
		gap: 3px;
		padding: 5px 10px;
		background: #FFF7ED;
		border: 1px solid #FFEDD5;
		border-radius: 6px;
		color: #FF6A00;
		font-size: 11px;
		font-weight: 700;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-card-select:hover {
		background: #FF6A00;
		border-color: #FF6A00;
		color: #FFFFFF;
	}

	/* SKELETON */
	.skeleton-card {
		pointer-events: none;
	}

	.skeleton-img {
		width: 100%;
		height: 140px;
		background: #E2E8F0;
	}

	.skeleton-meta {
		padding: 12px;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.skeleton-line {
		height: 12px;
		background: #E2E8F0;
		border-radius: 4px;
	}

	.w-40 { width: 40%; }
	.w-60 { width: 60%; }
	.w-90 { width: 90%; }

	.shimmer {
		animation: pulse 1.5s infinite;
	}

	@keyframes pulse {
		0%, 100% { opacity: 1; }
		50% { opacity: 0.5; }
	}

	/* EMPTY STATE */
	.catalog-empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		text-align: center;
		padding: 60px 20px;
		max-width: 480px;
		margin: 0 auto;
	}

	.empty-icon-wrap {
		width: 64px;
		height: 64px;
		border-radius: 16px;
		background: #F1F5F9;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 16px;
	}

	.catalog-empty-state h3 {
		margin: 0 0 8px;
		font-size: 16px;
		font-weight: 800;
		color: #0F172A;
	}

	.catalog-empty-state p {
		margin: 0 0 20px;
		font-size: 13px;
		color: #64748B;
		line-height: 1.5;
	}

	.btn-action-clear-filter {
		padding: 8px 18px;
		background: #0F172A;
		color: #FFFFFF;
		border: none;
		border-radius: 8px;
		font-size: 13px;
		font-weight: 700;
		cursor: pointer;
	}

	.error-state .empty-icon-wrap {
		background: #FEE2E2;
	}

	.empty-actions {
		display: flex;
		gap: 10px;
	}

	.btn-action-retry {
		padding: 8px 18px;
		background: #FF6A00;
		color: #FFFFFF;
		border: none;
		border-radius: 8px;
		font-size: 13px;
		font-weight: 700;
		cursor: pointer;
	}

	.btn-action-close {
		padding: 8px 18px;
		background: #F1F5F9;
		color: #475569;
		border: none;
		border-radius: 8px;
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;
	}

	/* PAGINATION */
	.catalog-pagination-bar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 12px 24px;
		background: #FFFFFF;
		border-top: 1px solid #E2E8F0;
		flex-shrink: 0;
	}

	.pagination-info {
		font-size: 12px;
		color: #64748B;
	}

	.pagination-controls {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.btn-page-step {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 14px;
		background: #FFFFFF;
		border: 1px solid #CBD5E1;
		border-radius: 8px;
		font-size: 12px;
		font-weight: 700;
		color: #0F172A;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-page-step:hover:not(:disabled) {
		background: #F8FAFC;
		border-color: #94A3B8;
	}

	.btn-page-step:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}

	/* ========================================================
	   TELA DE PREVIEW / CONFIRMAÇÃO
	   ======================================================== */
	.catalog-preview-container {
		display: flex;
		flex-direction: column;
		padding: 20px 24px;
		gap: 16px;
		overflow-y: auto;
		flex: 1;
	}

	.preview-header-nav {
		display: flex;
		align-items: center;
	}

	.btn-back-to-list {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: transparent;
		border: none;
		color: #64748B;
		font-size: 13px;
		font-weight: 700;
		cursor: pointer;
		padding: 6px 0;
	}

	.btn-back-to-list:hover {
		color: #0F172A;
	}

	.preview-main-layout {
		display: grid;
		grid-template-columns: 320px 1fr;
		gap: 28px;
		background: #FFFFFF;
		border: 1px solid #E2E8F0;
		border-radius: 14px;
		padding: 24px;
	}

	.preview-photo-col {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.preview-large-photo-box {
		width: 100%;
		height: 280px;
		border-radius: 12px;
		overflow: hidden;
		background: #F8FAFC;
		border: 1px solid #E2E8F0;
		position: relative;
	}

	.preview-large-img {
		width: 100%;
		height: 100%;
		object-fit: contain;
		padding: 12px;
	}

	.preview-photo-fallback {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 8px;
		color: #94A3B8;
		font-size: 12px;
	}

	.preview-photo-caption {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 11px;
		color: #059669;
		font-weight: 600;
	}

	.preview-details-col {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.meta-tags-row {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		margin-bottom: 6px;
	}

	.preview-tag-cat, .preview-tag-brand, .preview-tag-sku {
		padding: 3px 8px;
		border-radius: 6px;
		font-size: 11px;
		font-weight: 700;
	}

	.preview-tag-cat {
		background: #FFF7ED;
		color: #FF6A00;
		border: 1px solid #FFEDD5;
		text-transform: uppercase;
	}

	.preview-tag-brand {
		background: #F1F5F9;
		color: #0F172A;
		border: 1px solid #E2E8F0;
	}

	.preview-tag-sku {
		background: #F8FAFC;
		color: #64748B;
		border: 1px solid #E2E8F0;
		font-family: monospace;
	}

	.preview-product-name {
		margin: 0;
		font-size: 20px;
		font-weight: 800;
		color: #0F172A;
		line-height: 1.3;
	}

	/* CARD DE AVISO DE PREÇO */
	.preview-price-notice-card {
		display: flex;
		gap: 12px;
		padding: 12px 16px;
		background: #FFF7ED;
		border: 1px solid #FFEDD5;
		border-radius: 10px;
	}

	.notice-icon-col {
		padding-top: 2px;
		flex-shrink: 0;
	}

	.notice-text-col {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.notice-title-row {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 13px;
		color: #0F172A;
	}

	.notice-price-highlight {
		font-size: 14px;
		font-weight: 800;
		color: #FF6A00;
	}

	.notice-text-col p {
		margin: 0;
		font-size: 11px;
		color: #9A3412;
		line-height: 1.4;
	}

	/* CHECKLIST */
	.preview-checklist-box {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 14px 16px;
		background: #F8FAFC;
		border: 1px solid #E2E8F0;
		border-radius: 10px;
	}

	.checklist-title {
		margin: 0 0 4px;
		font-size: 12px;
		font-weight: 700;
		color: #475569;
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}

	.check-option-row {
		display: flex;
		align-items: flex-start;
		gap: 10px;
		cursor: pointer;
		padding: 6px 8px;
		border-radius: 6px;
		transition: background 0.15s ease;
	}

	.check-option-row:hover:not(.disabled) {
		background: #EDF2F7;
	}

	.check-option-row.disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.check-option-row input[type="checkbox"] {
		margin-top: 2px;
		accent-color: #FF6A00;
		width: 16px;
		height: 16px;
		cursor: pointer;
	}

	.check-option-info {
		display: flex;
		flex-direction: column;
	}

	.check-option-info strong {
		font-size: 13px;
		color: #0F172A;
	}

	.check-option-info small {
		font-size: 11px;
		color: #64748B;
	}

	/* ACTIONS */
	.preview-actions-bar {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 12px;
		margin-top: 8px;
	}

	.btn-cancel-preview {
		padding: 10px 18px;
		background: #FFFFFF;
		border: 1px solid #CBD5E1;
		border-radius: 8px;
		font-size: 13px;
		font-weight: 700;
		color: #475569;
		cursor: pointer;
	}

	.btn-cancel-preview:hover {
		background: #F1F5F9;
		color: #0F172A;
	}

	.btn-confirm-apply {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 10px 22px;
		background: #FF6A00;
		border: 1px solid #FF6A00;
		border-radius: 8px;
		font-size: 13px;
		font-weight: 800;
		color: #FFFFFF;
		cursor: pointer;
		box-shadow: 0 2px 6px rgba(255, 106, 0, 0.25);
		transition: all 0.15s ease;
	}

	.btn-confirm-apply:hover {
		background: #E85A00;
		transform: translateY(-1px);
		box-shadow: 0 4px 10px rgba(255, 106, 0, 0.3);
	}

	/* ========================================================
	   RESPONSIVIDADE (360px, 768px, 1024px, 1440px)
	   ======================================================== */
	
	/* 1024px */
	@media (max-width: 1024px) {
		.catalog-grid {
			grid-template-columns: repeat(3, 1fr);
		}
		.modal-box-catalog {
			width: 95%;
			height: 92vh;
		}
		.preview-main-layout {
			grid-template-columns: 260px 1fr;
			gap: 20px;
		}
	}

	/* 768px (Tablet) */
	@media (max-width: 768px) {
		.catalog-grid {
			grid-template-columns: repeat(2, 1fr);
			gap: 12px;
		}
		.preview-main-layout {
			grid-template-columns: 1fr;
			gap: 18px;
			padding: 16px;
		}
		.preview-large-photo-box {
			height: 220px;
		}
		.catalog-header {
			padding: 14px 18px;
		}
		.catalog-toolbar, .catalog-results-area {
			padding: 14px 18px;
		}
	}

	/* 360px - 480px (Mobile Estrito) */
	@media (max-width: 480px) {
		.modal-overlay-catalog {
			padding: 0;
		}
		.modal-box-catalog {
			width: 100%;
			height: 100%;
			max-height: 100vh;
			border-radius: 0;
		}
		.catalog-grid {
			grid-template-columns: 1fr;
			gap: 12px;
		}
		.catalog-title {
			font-size: 15px;
		}
		.catalog-subtitle {
			display: none;
		}
		.catalog-toolbar {
			padding: 12px 14px;
			gap: 8px;
		}
		.catalog-results-area {
			padding: 12px 14px;
		}
		.preview-large-photo-box {
			height: 180px;
		}
		.preview-actions-bar {
			flex-direction: column-reverse;
			width: 100%;
		}
		.btn-cancel-preview, .btn-confirm-apply {
			width: 100%;
			justify-content: center;
		}
		.catalog-pagination-bar {
			flex-direction: column;
			gap: 8px;
			align-items: stretch;
			text-align: center;
			padding: 10px 14px;
		}
		.pagination-controls {
			justify-content: center;
		}
	}
</style>
